'use client';
import {
  getAppointmentsForPlaceAndDate,
  selectStartDateTimeGetInitialStartDateTime,
} from 'Common/functions/SelectStartDateTime';
import Link from 'next/link';
import ServiceProviderAvailability from 'Common/components/ServiceProviderAvailability';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { getAvailableTimeSlotsInDateTimeRange, getDateSlotsWithAvailabilityForUpcomingMonth, getServiceProviderHasOpenSlotsForDateFn } from 'Common/components/ServiceProviderAvailability/functions';
import { CancelledStatus, CompletedStatus, LocationType, ServiceOfferingType, useGetAppointmentsForServiceProviderAtSelectedLocationQuery, useGetServiceProviderRetailLocationsQuery, useGetUserWithLocationsQuery, useUpdateAppointmentCartServiceAreaUidMutation, useUpdateAppointmentCartStartDateTimeMutation, useUpdateBookingCartServiceOfferingUidMutation, useUpdateBookingCartStartDateTimeMutation } from 'Generated/graphql';
import useServiceProviderCalendarUnavailableDateTimeBoundsForAppointment from 'Common/hooks/useServiceProviderCalendarUnavailableDateTimeBoundsForAppointment';
import AnonymousUserNavigation from '../AnonymousUserNavigation';
import CustomerNavigation from '../server/CustomerNavigation';
import { customerNavItems } from '../server/NavigationItems/items';
import dayjs from 'dayjs';
import { GetServiceOfferingWithCustomerInputs } from 'Common/functions/getServiceOfferingWithCustomerInputs';
import { calculateTotalsForServiceOfferingAndCustomerInputSelections } from 'Common/hooks/useTotalPriceAndDurationForBooking/fn';
import { getAppointmentRequestSummaryV3Url } from 'Common/functions/EnterCustomerInputsPage';
import useBookingCartCookie from 'Common/hooks/useBookingCartCookie';
import { AppCookie, getCookie, setCookie } from 'Common/functions/Cookie';
import useAppointmentCartCookie from 'Common/hooks/useAppointmentCartCookie';
import { ensureBookingCartCookieClientSide, getOrInitializeBookingCartUid } from 'Common/functions/BookingCart';
import Bugsnag from '@bugsnag/js';
import { useApolloClient } from '@apollo/client';
import { ensureAppointmentCartCookieClientSide, getOrInitializeAppointmentCartUid } from 'Common/functions/AppointmentCart';
import RequestAppointmentSteps from '../RequestAppointmentSteps';
import SchedulingFlowLayout from '../SchedulingFlowLayout';
import { getDropoffDeltaDays } from 'Common/functions/serviceProviderPreference';
import { getDropoffMessageCopy } from 'Common/functions/dropoff';
import StartDateTimePageContent from '../StartDateTimePageContent';
import { getInitialSelectedLocationId } from 'Common/functions/LocationCookie';
import { dedupeLocations, isLocationWithinBounds } from 'Common/functions/Location';
import ServiceProviderOutOfRangeAlert from '../ServiceProviderOutOfRangeAlert';
import { GetServiceProviderWithAvailabilityAndLocation } from 'Common/functions/getServiceProviderWithAvailabilityAndLocation';
import CustomerLocationSelectSmall from '../CustomerLocationSelectSmall';
import getTravelTimes, { getTimeSlotsFilteredByTravelTime } from 'Common/functions/getTravelTimes';
import signInAnonymously from 'Common/functions/signInAnonymously';
import { getAuth } from 'firebase/auth';
import ServiceProviderPagePromptCreateLocation from '../ServiceProviderPagePromptCreateLocation';

const now = Math.floor((new Date).getTime() / 1000);

const defaultCheckAvailabilityStartBoundry = dayjs().unix();
const defaultCheckAvailabilityEndBoundry = dayjs().add(1, 'M').unix();

type RequestAppointmentStartDateTimePageProps = {
  serviceProvider: GetServiceProviderWithAvailabilityAndLocation;
  serviceProviderId: string;
  serviceOffering: GetServiceOfferingWithCustomerInputs;
}

const RequestAppointmentStartDateTimePage: React.FC<RequestAppointmentStartDateTimePageProps> = ({
  serviceProvider,
  serviceProviderId,
  serviceOffering,
}) => {
  const getUserQuery = useGetUserWithLocationsQuery({
    fetchPolicy: 'no-cache',
  });
  const isTemporaryUser = getUserQuery.data?.getUser.isTemporary ?? true;
  const allLocations = getUserQuery.data?.getUser.locations;
  const isLoggedIn = Boolean(getUserQuery.data?.getUser.uid);
  const { data: spLocsData } = useGetServiceProviderRetailLocationsQuery({
    variables: {
      serviceProviderId: serviceProvider.id,
    }
  });
  const serviceProvideRetailLocations = spLocsData?.getServiceProviderRetailLocations;

  const recommendedStartDateTimeRaw = getCookie(AppCookie.CustomerBookingRecommendedStartDateTime);
  const recommendedStartDateTime = recommendedStartDateTimeRaw ?
    parseInt(recommendedStartDateTimeRaw) :
    null;
  const [isPromptCreateLocationModelOpen, setIsPromptCreateLocationModelOpen] =
    useState(false);

  const defaultSelectedLocationId =
    getCookie(AppCookie.CustomerSelectedLocationId);
  const initialSelectedLocationId =
    getInitialSelectedLocationId(allLocations || [], defaultSelectedLocationId);
  const [selectedLocationId, setSelectedLocationId] =
    useState<string | null | undefined>(initialSelectedLocationId);

  const locations = useMemo(
    () => {
      if (allLocations) {
        const deduped = dedupeLocations(allLocations);
        return deduped;
      }
      return [];
    },
    [allLocations?.length],
  );

  const selectedLocation = allLocations?.find(l => l.id === selectedLocationId) ||
    serviceProvideRetailLocations?.find(l => l.id === selectedLocationId);

  const [updateBookingCartStartDateTime] =
    useUpdateBookingCartStartDateTimeMutation();
  const [updateBookingCartServiceOfferingUid] =
    useUpdateBookingCartServiceOfferingUidMutation();
  const [updateAppointmentCartStartDateTime] =
    useUpdateAppointmentCartStartDateTimeMutation();
  const [updateAppointmentCartServiceAreaUid] =
    useUpdateAppointmentCartServiceAreaUidMutation();

  const [appointmentCart, isPreLoadingAppointmentCart] =
    useAppointmentCartCookie(serviceProviderId);

  const [bookingCart, isPreLoadingBookingCart] =
    useBookingCartCookie(serviceProviderId);

  const [selectedServiceAreaUid, setSelectedServiceAreaUid] =
    useState<string | undefined>(undefined);
  const [selectedStartDateTime, setSelectedStartDateTime] =
    useState<number | undefined>(bookingCart?.bookingSelections.startDateTime);
  const [isAnonLoggingIn, setIsAnonLoggingIn] = useState(false);

  const ensureLoggedIn = async () => {
    if (isAnonLoggingIn) {
      return false;
    }

    if (!getUserQuery.data && !getUserQuery.error) {
      return false;
    }

    if (!isLoggedIn) {
      setIsAnonLoggingIn(true);
      await signInAnonymously(getAuth());
      await getUserQuery.refetch();
      setIsAnonLoggingIn(false);
    }

    return true;
  };

  useEffect(() => {
    if (getUserQuery.data?.getUser) {
      if (getUserQuery.data.getUser.locations.length === 0) {
        setIsPromptCreateLocationModelOpen(true);
      }
    }
  }, [getUserQuery.data?.getUser.locations.length]);

  useEffect(() => {
    if (initialSelectedLocationId && !selectedLocationId) {
      setSelectedLocationId(initialSelectedLocationId);
    }
  }, [initialSelectedLocationId]);

  useEffect(() => {
    if (!isLoggedIn && (getUserQuery.data || getUserQuery.error)) {
      ensureLoggedIn().then(isNowLoggedIn => {
        if (!isNowLoggedIn) {
          return;
        }
        setIsPromptCreateLocationModelOpen(true);
      });
    }
  }, [isLoggedIn, getUserQuery.data, getUserQuery.error]);

  useEffect(() => {
    if (appointmentCart?.appointmentSelections.serviceAreaUid) {
      setSelectedServiceAreaUid(
        appointmentCart.appointmentSelections.serviceAreaUid,
      );
    }
  }, [appointmentCart?.uid]);

  const [totalPrice, totalDuration] = useMemo(() => {
    if (!isPreLoadingBookingCart && serviceOffering) {
      if (bookingCart) {
        return calculateTotalsForServiceOfferingAndCustomerInputSelections(
          serviceOffering.customerInputs,
          [],
          serviceOffering,
          bookingCart.bookingSelections.customerInputSelections || [],
          bookingCart.bookingSelections.customerInputGroupSelections,
        );
      }

      return [serviceOffering.basePrice, serviceOffering.baseTime];
    }

    return [null, null];
  }, [bookingCart, serviceOffering, isPreLoadingBookingCart]);

  const totalDurationSeconds = totalDuration ? totalDuration * 60 : 0;

  const spDayOfWeekAvailability = serviceProvider.dayOfWeekAvailability;
  const spDateAvailability = serviceProvider.dateAvailability;

  const startDateTime = selectStartDateTimeGetInitialStartDateTime(
    now,
    selectedStartDateTime,
    recommendedStartDateTime,
  );

  const [monthUnix, setMonthUnix] = useState(
    startDateTime ? dayjs.unix(startDateTime).startOf('M').unix() : undefined
  );

  const checkAvailabilityStartBoundry = useMemo(() => {
    if (startDateTime) {
      const dateDayjs = dayjs.unix(startDateTime).startOf('d').subtract(1, 'M');
      return dateDayjs.unix();
    }
    return defaultCheckAvailabilityStartBoundry;
  }, [startDateTime]);

  const checkAvailabilityEndBoundry = useMemo(() => {
    if (startDateTime) {
      const dateDayjs = dayjs.unix(startDateTime).startOf('d').add(1, 'M');
      return dateDayjs.unix();
    }
    return defaultCheckAvailabilityEndBoundry;
  }, [startDateTime]);

  const serviceProviderUid = serviceProvider.ownerUid;
  const unavailableTimeBounds =
    useServiceProviderCalendarUnavailableDateTimeBoundsForAppointment(
      serviceProviderUid,
      selectedLocation?.uid,
      checkAvailabilityStartBoundry,
      checkAvailabilityEndBoundry,
    );

  const serviceProviderAppointmentsQuery = useGetAppointmentsForServiceProviderAtSelectedLocationQuery({
    variables: {
      userUid: serviceProviderUid,
      query: {
        timeUtcSecondsBounds: {
          min: dayjs().unix(),
        },
        completeStatus: CompletedStatus.Incomplete,
        cancelledStatus: CancelledStatus.NotCancelled,
      },
    }
  });

  const serviceProviderAppointments =
    serviceProviderAppointmentsQuery.data?.getAppointmentsForUser;

  const datesWithExistingAppointmentsAtSelectedLocation = selectedLocation && serviceProviderAppointments ?
    getAppointmentsForPlaceAndDate(
      serviceProviderAppointments,
      selectedLocation.placeId,
      now,
    ) : [];

  const client = useApolloClient();

  const hooks = {
    onSelectTimeSlot: async (v: number) => {
      if (!isLoggedIn) {
        const isNowLoggedIn = await ensureLoggedIn();
        if (!isNowLoggedIn) {
          return;
        }
      }

      if (v && serviceOffering) {
        const appointmentCartUid = await getOrInitializeAppointmentCartUid(
          client.mutate,
          appointmentCart?.uid,
          serviceProviderUid,
        );
        if (appointmentCartUid === null) {
          Bugsnag.notify(new Error('Failed to init appointment cart uid'));
          return;
        }

        ensureAppointmentCartCookieClientSide(
          serviceProviderId,
          appointmentCart?.uid,
          appointmentCartUid,
        );

        updateAppointmentCartStartDateTime(
          {
            variables: {
              appointmentCartUid,
              startDateTime: v,
            }
          }
        );

        const bookingCartUid = await getOrInitializeBookingCartUid(
          client.mutate,
          bookingCart?.uid,
          serviceProviderUid,
          serviceOffering.uid,
        );
        if (bookingCartUid === null) {
          Bugsnag.notify(new Error('Failed to init booking cart uid'));
          return;
        }

        ensureBookingCartCookieClientSide(
          serviceProviderId,
          bookingCart?.uid,
          bookingCartUid,
        );

        await updateBookingCartServiceOfferingUid({
          variables: {
            bookingCartUid,
            serviceOfferingUid: serviceOffering.uid,
          }
        });

        await updateBookingCartStartDateTime(
          {
            variables: {
              bookingCartUid,
              startDateTime: v,
            }
          },
        );

        setSelectedStartDateTime(v);
      }
    },
  };

  const getAllowNewBookingWithinAvailabilityBuffer = (d: number) => {
    const checkDate = dayjs.unix(d);
    const isAllowed =
      datesWithExistingAppointmentsAtSelectedLocation.some(
        ad => {
          const sameLocationSameDateAppointmentStartDateTime =
            dayjs.unix(ad.startDateTime);
          const isSameDate =
            sameLocationSameDateAppointmentStartDateTime
              .isSame(checkDate, 'd');
          return isSameDate;
        }
      );
    return isAllowed;
  };

  const durationSeconds =
    serviceOffering ? serviceOffering.type === ServiceOfferingType.PickupDropOff ?
      300 : // set pickups to 5mins (this hides the bug in isBoundsIntersectingBoundsList) TODO: fix this properly likely will need a rewrite
      totalDurationSeconds : 0;

  const getTimeSlots = (
    f: number,
    t: number,
  ) => getAvailableTimeSlotsInDateTimeRange(
    serviceProviderUid,
    spDayOfWeekAvailability,
    spDateAvailability,
    unavailableTimeBounds || [],
    f,
    t,
    durationSeconds,
    false,
  );

  const disabled = !unavailableTimeBounds;
  const dateSlots = useMemo(() => {
    const checkFromDate = monthUnix ||
      checkAvailabilityStartBoundry;

    const slots = getDateSlotsWithAvailabilityForUpcomingMonth(
      checkFromDate,
      getServiceProviderHasOpenSlotsForDateFn(
        getAllowNewBookingWithinAvailabilityBuffer,
        getTimeSlots,
      ),
    );

    return slots;
  }, [monthUnix, disabled]);

  const serviceProviderAvailabilityNode = unavailableTimeBounds ? (
    <>
      <span className="text-2xl font-bold">
        Choose a date
      </span>
      <ServiceProviderAvailability
        disabled={disabled || isPreLoadingAppointmentCart || isPreLoadingBookingCart || !selectedLocation || totalDuration === null}
        initialValue={startDateTime}
        onSelectDate={value => {
        }}
        onSelectTime={value => {
          return hooks?.onSelectTimeSlot &&
            hooks.onSelectTimeSlot(value);
        }}
        getTimeSlots={async (f, t) => {
          if (totalDuration && selectedLocation) {
            const ts = getTimeSlots(f, t);
            const tts = await getTravelTimes(
              client.query,
              undefined,
              serviceProviderUid,
              selectedLocation.uid,
              ts,
              totalDuration,
            );

            const slots = getTimeSlotsFilteredByTravelTime(
              ts,
              tts || [],
              totalDuration,
            );

            return slots;
          }

          return null;
        }}
        dateSlots={dateSlots}
        onSelectMonth={m => setMonthUnix(m)}
        recommendedStartDateTime={recommendedStartDateTime ?? undefined}
      />
    </>
  ) : <></>;

  const navigation = (
    <div>
      {isTemporaryUser ? (
        <AnonymousUserNavigation />
      ) : (
        <CustomerNavigation items={customerNavItems} />
      )}
      <RequestAppointmentSteps
        currentStep={2}
        innerClass="sm:px-4 md:px-48"
        serviceProviderId={serviceProviderId}
        serviceOfferingUid={serviceOffering.uid}
      />
    </div>
  );

  const isLocationInBounds = useMemo(
    () => {
      if (selectedLocation && serviceProvider) {
        if (selectedLocation.type === LocationType.ServiceProviderRetail) {
          return true;
        }

        return isLocationWithinBounds(
          selectedLocation.latitude,
          selectedLocation.longitude,
          serviceProvider.location.discoverableBounds,
        );
      }
      return null;
    },
    [selectedLocation?.latitude, serviceProvider?.location.discoverableBounds.min]
  );

  const alerts = isLocationInBounds === false && (
    <ServiceProviderOutOfRangeAlert />
  );

  const isDisabled = !startDateTime || !isLocationInBounds;

  const nextButton = (
    <Link
      href={getAppointmentRequestSummaryV3Url(
        serviceProviderId,
      )}
      className={isDisabled ? 'pointer-events-none' : ''}
    >
      <button
        className={`btn-primary w-full py-4 my-8 ${isDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
        disabled={isDisabled}
      >
        Review Selections
      </button>
    </Link>
  );

  const disabledNextButton = (
    <Link
      href="#"
      className="mb-4 w-full block disabled"
    >
      <button className="btn-primary w-full py-4">
        Selected Location Out Of Range
      </button>
    </Link>
  );

  const isPickup =
    serviceOffering.type === ServiceOfferingType.PickupDropOff;
  let durationCopy: string | undefined = '';
  if (isPickup && serviceProvider?.preferences) {
    const dropoffDeltaDays =
      getDropoffDeltaDays(dayjs(), serviceProvider.preferences);
    durationCopy =
      getDropoffMessageCopy(dropoffDeltaDays);
  }

  const combinedLocations = {
    'My Locations': locations,
    'Service Provider Locations': serviceProvideRetailLocations || [],
  };

  return (
    <>
      <SchedulingFlowLayout
        navigation={navigation}
      >
        <StartDateTimePageContent
          precontent={alerts}
          nextButton={isLocationInBounds ? nextButton : disabledNextButton}
          durationCopy={durationCopy}
          bookingCart={bookingCart}
          serviceProvider={serviceProvider}
          availabilityNode={serviceProviderAvailabilityNode}
          selectedServiceAreaUid={selectedServiceAreaUid}
          totalPrice={totalPrice}
          durationSeconds={durationSeconds}
          serviceOffering={serviceOffering}
          startDateTime={startDateTime}
          location={selectedLocation}
          onServiceAreaSelect={async (saUid) => {
            if (saUid) {
              setSelectedServiceAreaUid(saUid);

              const appointmentCartUid = await getOrInitializeAppointmentCartUid(
                client.mutate,
                appointmentCart?.uid,
                serviceProviderUid,
              );
              if (appointmentCartUid === null) {
                Bugsnag.notify(new Error('Failed to init appointment cart uid'));
                return;
              }

              ensureAppointmentCartCookieClientSide(
                serviceProviderId,
                appointmentCart?.uid,
                appointmentCartUid,
              );

              await updateAppointmentCartServiceAreaUid(
                {
                  variables: {
                    appointmentCartUid,
                    serviceAreaUid: saUid,
                  }
                },
              );
            }
          }}
          locationNode={locations.length > 0 && (
            <CustomerLocationSelectSmall
              locations={combinedLocations}
              value={selectedLocationId || locations[0].id}
              onSelect={id => {
                if (id !== selectedLocationId) {
                  setSelectedLocationId(id);
                  setCookie(AppCookie.CustomerSelectedLocationId, id, 14);
                }
              }}
            />
          )}
        />
      </SchedulingFlowLayout>
      <Suspense>
        <ServiceProviderPagePromptCreateLocation
          isDisabled={!isLoggedIn}
          onLocationSelected={id => {
            setSelectedLocationId(id);
            setCookie(AppCookie.CustomerSelectedLocationId, id, 14);
            getUserQuery.refetch();
            setIsPromptCreateLocationModelOpen(false);
          }}
          redirectTo={() => {
            return null;
          }}
          isOpen={isPromptCreateLocationModelOpen}
          onClose={() => setIsPromptCreateLocationModelOpen(false)}
        />
      </Suspense>
    </>
  );
};

export default RequestAppointmentStartDateTimePage;

